import { inject, Service } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, first, map, mergeMap } from 'rxjs/operators';
import { IAdvertisement } from '../../common/interfaces/advertisement.interface';
import { IBranding } from '../../common/interfaces/branding.interface';
import { IGeneralVenueInfo } from '../../core/interfaces/general.interface';
import { FingerprintService } from '../../core/providers/fingerprint/fingerprint.service';
import { LayoutService } from '../../core/providers/layout/layout.service';
import { SurveyService } from '../survey/survey.service';
import { IWheelOfFortuneConfig } from '../wheel-of-fortune/interfaces/wheel-of-fortune.interface';
import { WheelOfFortuneService } from '../wheel-of-fortune/wheel-of-fortune.service';
import { CampaignService } from './campaign.service';
import { ICampaign } from './interfaces/campaign.interface';

@Service()
export class ExactCampaignResolver {
  private campaignService = inject(CampaignService);
  private layoutService = inject(LayoutService);
  private titleService = inject(Title);
  private wheelOfFortuneService = inject(WheelOfFortuneService);
  private fingerprintService = inject(FingerprintService);
  private surveyService = inject(SurveyService);

  resolve(route: ActivatedRouteSnapshot): Observable<ICampaign> {
    const { venueId, campaignId } = route.params;

    return this.fingerprintService.visitorId$().pipe(
      first(),
      mergeMap((visitorId: string) => this.getExactCampaign(venueId, campaignId, visitorId))
    );
  }

  private getExactCampaign(
    venueId: string,
    campaignId: string,
    visitorId: string
  ): Observable<ICampaign> {
    this.surveyService.setCampaignSurvey(venueId, visitorId);

    return this.campaignService.fetchExactCampaign$(venueId, campaignId, visitorId).pipe(
      filter(Boolean),
      map(response => {
        this.setLayoutCustomSettings(response.info);
        if (response.info.wheel) {
          this.setStatusWheelOfFortune(response.info.wheel);
          this.wheelOfFortuneService.isWheelBlocked = false;
          this.wheelOfFortuneService.venueUuid = venueId;
        }

        return response;
      })
    );
  }

  private setLayoutCustomSettings(info: IGeneralVenueInfo): void {
    this.setAppColour(info.branding);
    this.setVenueLogo(info.branding);
    this.setTabName(info.venueName);
    this.setAdvertisement(info.advertisement);
  }

  private setAppColour(branding: IBranding | null): void {
    if (branding) {
      this.layoutService.setLayoutDefaultColor(branding.colorTint);
    }
  }

  private setVenueLogo(branding: IBranding | null): void {
    if (branding) {
      this.layoutService.setVenueLogo(branding.logoFileUuid);
    }
  }

  private setAdvertisement(advertisement: IAdvertisement | null | undefined): void {
    if (advertisement) {
      this.layoutService.setAdvertisementURL(advertisement);
    }
  }

  private setTabName(venueName: string): void {
    const tabTitle: string = venueName ? `${venueName} - Foodback` : 'Foodback';

    this.titleService.setTitle(tabTitle);
    this.layoutService.setVenueName(venueName);
  }

  private setStatusWheelOfFortune(wheel: IWheelOfFortuneConfig): void {
    this.wheelOfFortuneService.setWheelOfFortuneEnable(wheel.isActive);
    this.wheelOfFortuneService.prizeText = wheel.prizeText;
  }
}
